import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, Clock, AlertTriangle, Calendar, Flame, Target, Trophy, Zap, Plus, BarChart3, TrendingUp, X } from 'lucide-react';
import { useUserData } from '@/hooks/useUserData';
import { AllTasksView } from '@/components/dashboard/all-tasks-view';
import { dailyTaskService } from '@/services/dailyTaskService';


interface StreakDay {
  date: string;
  completed: boolean;
  tasksCompleted: number;
  totalTasks: number;
}

const BossMode: React.FC = () => {
  // Live user data
  const {
    profile = null,
    tasks = [],
    loading = false,
    error = null,
    streakDays = 0,
    daysRemaining = 30,
    toggleTask = async () => false,
    addTask = async () => null,
  } = useUserData() || {};
  
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskCategory, setNewTaskCategory] = useState<'daily' | 'weekly'>('daily');
  const [showAllTasks, setShowAllTasks] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [weeklyGoal, setWeeklyGoal] = useState('');

  // Generate streak data for the current week (Sunday to Saturday)
  const generateStreakData = (): StreakDay[] => {
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Calculate start of week (Sunday)
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - currentDay);
    startOfWeek.setHours(0, 0, 0, 0);
    
    const data: StreakDay[] = [];
    
    // Generate 7 days starting from Sunday
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(startOfWeek);
      currentDate.setDate(startOfWeek.getDate() + i);
      const currentDateStr = currentDate.toISOString().split('T')[0];
      
      // Get all tasks for the current date using ISO date string comparison
      const dayTasks = tasks.filter(t => {
        if (!t.due_date) return false;
        const taskDate = new Date(t.due_date).toISOString().split('T')[0];
        return taskDate === currentDateStr;
      });
      
      const completedTasks = dayTasks.filter(t => t.completed);
      
      data.push({
        date: currentDateStr,
        completed: completedTasks.length > 0 && completedTasks.length === dayTasks.length,
        tasksCompleted: completedTasks.length,
        totalTasks: dayTasks.length,
      });
    }
    
    return data;
  };

  const streakData = generateStreakData();
  const currentStreak = streakDays;
  const longestStreak = streakDays;

  const getNextSaturday = () => {
    const today = new Date();
    const day = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysUntilSaturday = day === 6 ? 7 : (6 - day); // If today is Saturday, schedule for next Saturday
    const nextSaturday = new Date(today);
    nextSaturday.setDate(today.getDate() + daysUntilSaturday);
    return nextSaturday.toISOString().split('T')[0]; // Return as YYYY-MM-DD
  };

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return;
    
    const taskData: any = {
      title: newTaskTitle,
      description: 'Custom task',
      completed: false,
      priority: 'medium',
      due_date: todayStr // Set due date to today for custom tasks
    };
    
    await addTask(taskData);
    setNewTaskTitle('');
  };

  const handleSetWeeklyGoal = async () => {
    if (!weeklyGoal.trim()) return;
    
    await addTask({
      title: `Weekly Goal: ${weeklyGoal}`,
      description: 'Personal weekly objective',
      completed: false,
      priority: 'high',
      category: 'weekly',
      due_date: getNextSaturday() // Set due date to next Saturday
    });
    setWeeklyGoal('');
    setShowGoalModal(false);
  };

  // Get today's date at midnight for accurate comparison
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  
  // Get the current day of week (0 = Sunday, 1 = Monday, etc.)
  const day = today.getDay();
  
  // Calculate start of week (Sunday)
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - day);
  startOfWeek.setHours(0, 0, 0, 0);
  
  // Calculate end of week (Saturday)
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  // Load daily tasks
  const [todaysDailyTasks, setTodaysDailyTasks] = useState([]);
  
  useEffect(() => {
    const loadDailyTasks = async () => {
      if (!profile?.user_id) return;
      try {
        await dailyTaskService.createDailyTasksIfNeeded(profile.user_id);
        const daily = await dailyTaskService.getTodaysDailyTasks(profile.user_id);
        setTodaysDailyTasks(daily || []);
      } catch (error) {
        console.error('Error loading daily tasks:', error);
      }
    };
    loadDailyTasks();
  }, [profile?.user_id]);
  
  // Process all tasks and ensure no duplicates
  const processedTasks = useMemo(() => {
    // Create a map to store tasks, using a combination of title and type to ensure uniqueness
    const taskMap = new Map();
    
    // Daily task titles to identify and deduplicate
    const dailyTaskTitles = [
      'Enter Journal Details',
      'Post on Social Media'
    ];
    
    // First, add all regular tasks (non-daily tasks)
    tasks.forEach(task => {
      if (task.id && !dailyTaskTitles.includes(task.title)) {
        taskMap.set(task.id, { ...task, isDailyTask: false });
      }
    });
    
    // Then, add daily tasks with unique keys based on title to prevent duplicates
    todaysDailyTasks.forEach(dailyTask => {
      if (dailyTask.title && dailyTaskTitles.includes(dailyTask.title)) {
        // Use title as key for daily tasks to ensure we only have one of each type
        const uniqueKey = `daily_${dailyTask.title.toLowerCase().replace(/\s+/g, '_')}`;
        taskMap.set(uniqueKey, { 
          ...dailyTask, 
          isDailyTask: true,
          originalId: dailyTask.id, // Preserve the actual database ID
          id: uniqueKey // Use synthetic key for UI uniqueness
        });
      }
    });
    
    return Array.from(taskMap.values());
  }, [tasks, todaysDailyTasks]);
  
  // Categorize tasks without duplicates
  const [overdueTasks, todayTasks, thisWeeksTasks] = useMemo(() => {
    const overdue = [];
    const today = [];
    const week = [];
    const now = new Date();
    
    // Create today's date at midnight for accurate comparison
    const todayAtMidnight = new Date();
    todayAtMidnight.setHours(0, 0, 0, 0);
    
    // Format today's date as DD/MM/YYYY
    const formattedDate = todayAtMidnight.toLocaleDateString('en-GB');
    
    const dailyTaskTitles = [
      'Enter Journal Details',
      'Post on Social Media'
    ];
    
    // Separate daily tasks from other tasks
    const [dailyTasks, otherTasks] = processedTasks.reduce((acc, task) => {
      if (dailyTaskTitles.includes(task.title)) {
        // Create a new task object with the date in the title
        const taskWithDate = {
          ...task,
          title: `${task.title} (${formattedDate})`,
          originalTitle: task.title // Keep original title for reference
        };
        acc[0].push(taskWithDate);
      } else {
        acc[1].push(task);
      }
      return acc;
    }, [[], []]);
    
    // Add daily tasks to today's list
    today.push(...dailyTasks);
    
    // Process other tasks (excluding daily tasks)
    otherTasks.forEach(task => {
      if (!task.due_date) return;
      
      const dueDate = new Date(task.due_date);
      const taskDateAtMidnight = new Date(dueDate);
      taskDateAtMidnight.setHours(0, 0, 0, 0);
      
      // Check if task is due today (regardless of completion status)
      if (taskDateAtMidnight.getTime() === todayAtMidnight.getTime()) {
        today.push(task);
      }
      // Check if task is overdue (not completed and due before today)
      else if (!task.completed && taskDateAtMidnight < todayAtMidnight) {
        overdue.push(task);
      }
      // Check if task is due this week (but not today)
      else if (dueDate >= startOfWeek && dueDate <= endOfWeek) {
        week.push(task);
      }
    });
    
    return [overdue, today, week];
  }, [processedTasks, startOfWeek, endOfWeek]);
  
  // Alias for backward compatibility
  const dailyTasks = todayTasks;
  const weeklyTasks = [...todayTasks, ...thisWeeksTasks];
  
  // Calculate completed counts
  const dailyCompleted = dailyTasks.filter(task => task.completed).length;
  const weeklyCompleted = weeklyTasks.filter(task => task.completed).length;
  
  // For display in the stats, use all tasks regardless of category
  const allTasksCount = tasks.length;
  const allCompletedTasks = tasks.filter(task => task.completed).length;

  // Gate is handled by route-level IdeaValidationGate; avoid in-component gating to prevent stale locks

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-startup-slate rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-startup-slate rounded"></div>
              ))}
            </div>
            <div className="h-96 bg-startup-slate rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="card-startup border-startup-red bg-startup-red/5 text-center py-8">
            <AlertTriangle className="w-12 h-12 text-startup-red mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-startup-red mb-2">Error Loading Data</h3>
            <p className="text-startup-gray">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show all tasks view if requested
  if (showAllTasks) {
    return (
      <AllTasksView 
        tasks={tasks}
        onTaskToggle={toggleTask}
        onClose={() => setShowAllTasks(false)}
      />
    );
  }

  // Do not gate here; IdeaValidationGate already restricts access based on validation status

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'high': return 'border-startup-red bg-startup-red/5';
      case 'medium': return 'border-startup-gold bg-startup-gold/5';
      case 'low': return 'border-startup-gray bg-startup-gray/5';
      default: return 'border-startup-gray bg-startup-gray/5';
    }
  };

  const getPriorityDot = (priority?: string) => {
    switch (priority) {
      case 'high': return 'bg-startup-red';
      case 'medium': return 'bg-startup-gold';
      case 'low': return 'bg-startup-gray';
      default: return 'bg-startup-gray';
    }
  };

  

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="text-3xl font-bold text-startup-navy mb-2 flex items-center">
            <Zap className="w-8 h-8 mr-3 text-startup-gold" />
            Boss Mode 💪
          </h1>
          <p className="text-startup-gray">
            Take control of your day with focused execution and streak building • <span className="font-semibold text-startup-red">{daysRemaining} days remaining in your sprint</span>
          </p>
        </div>
        

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 animate-fade-in">
          <div className="card-startup text-center">
            <div className="flex items-center justify-center mb-2">
              <Flame className="w-6 h-6 text-startup-red mr-2" />
              <span className="text-2xl font-bold text-startup-navy">{currentStreak}</span>
            </div>
            <p className="text-startup-gray text-sm">Current Streak</p>
          </div>
          
          <div className="card-startup text-center">
            <div className="flex items-center justify-center mb-2">
              <Trophy className="w-6 h-6 text-startup-gold mr-2" />
              <span className="text-2xl font-bold text-startup-navy">{longestStreak}</span>
            </div>
            <p className="text-startup-gray text-sm">Longest Streak</p>
          </div>
          
          <div className="card-startup text-center">
            <div className="flex items-center justify-center mb-2">
              <CheckCircle className="w-6 h-6 text-startup-teal mr-2" />
              <span className="text-2xl font-bold text-startup-navy">{dailyCompleted}/{dailyTasks.length || 0}</span>
            </div>
            <p className="text-startup-gray text-sm">Daily Tasks</p>
          </div>
          
          <div className="card-startup text-center">
            <div className="flex items-center justify-center mb-2">
              <Target className="w-6 h-6 text-startup-peach mr-2" />
              <span className="text-2xl font-bold text-startup-navy">{weeklyCompleted}/{weeklyTasks.length || 0}</span>
            </div>
            <p className="text-startup-gray text-sm">Weekly Tasks</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Daily Tasks */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overdue Tasks Alert */}
            {overdueTasks.length > 0 && (
              <div className="card-startup border-2 border-startup-red bg-startup-red/5 animate-fade-in">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="w-6 h-6 text-startup-red mr-3" />
                  <h3 className="text-lg font-semibold text-startup-red">
                    Overdue Tasks ({overdueTasks.length})
                  </h3>
                </div>
                <div className="space-y-3">
                  {overdueTasks.map((task, index) => (
                    <div 
                      key={task.id}
                      className="flex items-center justify-between p-3 bg-white rounded-xl animate-fade-in"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => toggleTask(task.originalId || task.id)}
                          className="w-6 h-6 border-2 border-startup-red rounded-full flex items-center justify-center hover:bg-startup-red hover:text-white transition-colors"
                        >
                          {task.completed && <CheckCircle className="w-4 h-4" />}
                        </button>
                        <div>
                          <p className="font-medium text-startup-navy">{task.title}</p>
                          <p className="text-sm text-startup-red">
                            Overdue: {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                          </p>
                        </div>
                      </div>
                      <div className={`w-3 h-3 rounded-full ${getPriorityDot(task.priority)}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Daily Tasks */}
            <div className="card-startup animate-fade-in">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-startup-navy flex items-center">
                  <Clock className="w-6 h-6 mr-3 text-startup-teal" />
                  Today's Daily Tasks
                </h3>
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-startup-slate rounded-full h-2">
                    <div 
                      className="bg-startup-teal rounded-full h-2 transition-all duration-500"
                      style={{ width: `${dailyTasks.length > 0 ? (dailyTasks.filter(t => t.completed).length / dailyTasks.length) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="text-startup-teal font-medium text-sm">
                    {dailyTasks.length > 0 ? Math.round((dailyTasks.filter(t => t.completed).length / dailyTasks.length) * 100) : 0}%
                  </span>
                </div>
              </div>

              {/* Add Task Input */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="Add a new task..."
                  className="flex-1 px-3 py-2 border border-startup-gray/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-startup-teal"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
                />
                <button onClick={handleAddTask} className="px-4 py-2 bg-startup-teal text-white rounded-lg hover:bg-startup-teal/90 transition-colors">
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                {dailyTasks.length > 0 ? dailyTasks.filter(task => !overdueTasks.some(overdue => overdue.id === task.id)).map((task, index) => (
                  <div 
                    key={task.id}
                    className={`border-2 rounded-xl p-4 transition-all duration-300 hover:shadow-md animate-fade-in ${
                      task.completed ? 'bg-startup-teal/5 border-startup-teal' : getPriorityColor(task.priority)
                    } ${overdueTasks.includes(task) ? 'border-startup-red bg-startup-red/5' : ''}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="flex items-start space-x-4">
                      <button
                        onClick={() => toggleTask(task.originalId || task.id)}
                        className={`w-6 h-6 border-2 rounded-full flex items-center justify-center transition-all duration-300 ${
                          task.completed 
                            ? 'bg-startup-teal border-startup-teal text-white' 
                            : 'border-startup-gray hover:border-startup-teal'
                        }`}
                      >
                        {task.completed && <CheckCircle className="w-4 h-4" />}
                      </button>
                      
                      <div className="flex-1">
                        <h4 className={`font-medium ${task.completed ? 'text-startup-gray line-through' : 'text-startup-navy'}`}>
                          {task.title}
                        </h4>
                        <p className="text-startup-gray text-sm mt-1">{task.description}</p>
                        {overdueTasks.includes(task) && (
                          <p className="text-startup-red text-xs mt-1 font-medium">
                            Overdue: {task.due_date ? new Date(task.due_date).toLocaleDateString() : 'No due date'}
                          </p>
                        )}
                      </div>
                      
                      <div className={`w-3 h-3 rounded-full ${getPriorityDot(task.priority)}`} />
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-startup-gray">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium mb-1">No tasks for today!</p>
                    <p className="text-sm">Your tasks are scheduled based on your sprint plan. Check back tomorrow!</p>
                  </div>
                )}
              </div>
            </div>

            {/* Weekly Tasks */}
            <div className="card-startup animate-fade-in">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-startup-navy flex items-center">
                  <Calendar className="w-6 h-6 mr-3 text-startup-peach" />
                  This Week's Tasks
                </h3>
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-startup-slate rounded-full h-2">
                    <div 
                      className="bg-startup-peach rounded-full h-2 transition-all duration-500"
                      style={{ width: `${thisWeeksTasks.length > 0 ? (thisWeeksTasks.filter(t => t.completed).length / thisWeeksTasks.length) * 100 : 0}%` }}
                    />
                  </div>
                  <span className="text-startup-peach font-medium text-sm">
                    {thisWeeksTasks.length > 0 ? Math.round((thisWeeksTasks.filter(t => t.completed).length / thisWeeksTasks.length) * 100) : 0}%
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                {thisWeeksTasks.length > 0 ? thisWeeksTasks.map((task, index) => (
                  <div 
                    key={task.id}
                    className={`border-2 rounded-xl p-4 transition-all duration-300 hover:shadow-md animate-fade-in ${
                      task.completed ? 'bg-startup-gold/5 border-startup-gold' : getPriorityColor(task.priority)
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="flex items-start space-x-4">
                      <button
                        onClick={() => toggleTask(task.originalId || task.id)}
                        className={`w-6 h-6 border-2 rounded-full flex items-center justify-center transition-all duration-300 ${
                          task.completed 
                            ? 'bg-startup-gold border-startup-gold text-white' 
                            : 'border-startup-gray hover:border-startup-gold'
                        }`}
                      >
                        {task.completed && <CheckCircle className="w-4 h-4" />}
                      </button>
                      
                      <div className="flex-1">
                        <h4 className={`font-medium ${task.completed ? 'text-startup-gray line-through' : 'text-startup-navy'}`}>
                          {task.title}
                        </h4>
                        <p className="text-startup-gray text-sm mt-1">{task.description}</p>
                      </div>
                      
                      <div className={`w-3 h-3 rounded-full ${getPriorityDot(task.priority)}`} />
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-startup-gray">
                    <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium mb-1">No tasks this week!</p>
                    <p className="text-sm">Tasks are scheduled throughout your 30-day sprint.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Streak Calendar & Cashpurr */}
          <div className="space-y-6">
            {/* Streak Calendar */}
            <div className="card-startup animate-fade-in">
              <h3 className="text-xl font-semibold text-startup-navy flex items-center mb-6">
                <Calendar className="w-6 h-6 mr-3 text-startup-teal" />
                Streak Calendar
              </h3>

              <div className="grid grid-cols-7 gap-1 mb-4">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => {
                  const dayDate = new Date();
                  dayDate.setDate(dayDate.getDate() - (dayDate.getDay() - index));
                  const isToday = dayDate.toDateString() === new Date().toDateString();
                  const dayNumber = dayDate.getDate();
                  const dayData = streakData.find(d => new Date(d.date).toDateString() === dayDate.toDateString());
                  
                  return (
                    <div key={index} className="flex flex-col items-center">
                      <div className={`text-sm font-medium mb-1 ${
                        isToday ? 'text-startup-teal font-bold' : 'text-startup-gray'
                      }`}>
                        {day}
                      </div>
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${
                          dayData?.completed 
                            ? 'bg-startup-teal text-white' 
                            : isToday 
                              ? 'border border-startup-teal' 
                              : 'bg-startup-slate/50 text-startup-gray'
                        }`}
                      >
                        {dayNumber}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-startup-navy mb-1">🔥 {currentStreak} Days</div>
                <p className="text-startup-gray text-sm">Keep the momentum going!</p>
              </div>
            </div>

            {/* Cashpurr Motivation */}
            <div className="card-startup-dark animate-fade-in relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-startup-gold rounded-full flex items-center justify-center mr-4">
                    <span className="text-2xl">🐱</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Cashpurr Says</h3>
                    <p className="text-white/70 text-sm">Your AI Success Coach</p>
                  </div>
                </div>
                
                <div className="bg-white/10 rounded-xl p-4 mb-4">
                  <p className="text-white/90 text-sm leading-relaxed">
                    "Purr-fect work on that {currentStreak}-day streak! 🔥 You're in the zone now. 
                    Remember, consistency beats intensity - keep showing up every day and watch your startup dreams come true! 
                    Meow-ney is on the way! 💰"
                  </p>
                </div>

                <div className="flex items-center justify-between text-white/70 text-xs">
                  <span>Daily Motivation</span>
                  <span className="flex items-center">
                    <Flame className="w-3 h-3 mr-1" />
                    Streak Bonus Active
                  </span>
                </div>
              </div>

              <div className="absolute -right-4 -bottom-4 opacity-10">
                <div className="w-24 h-24 bg-startup-gold rounded-full animate-float" />
              </div>
            </div>

            {/* Quick Actions */}
            <div className="card-startup animate-fade-in">
              <h3 className="text-lg font-semibold text-startup-navy mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button 
                  onClick={() => setShowAllTasks(true)}
                  className="btn-startup-primary w-full text-left flex items-center justify-between p-3 rounded-lg"
                >
                  <span>📋 View All Tasks</span>
                  <span className="text-sm opacity-75">{tasks.length} total</span>
                </button>
                
                <button 
                  className="btn-startup-outline w-full text-left flex items-center justify-between p-3 rounded-lg"
                  onClick={() => setShowAnalytics(true)}
                >
                  <span>📊 View Analytics</span>
                  <span className="text-sm opacity-75">ROI & Growth</span>
                </button>
                
                <button 
                  className="btn-startup-outline w-full text-left flex items-center justify-between p-3 rounded-lg"
                  onClick={() => setShowGoalModal(true)}
                >
                  <span>🎯 Set Weekly Goal</span>
                  <span className="text-sm opacity-75">Stay focused</span>
                </button>
                
                <button 
                  className="btn-startup-secondary w-full text-left flex items-center justify-between p-3 rounded-lg"
                  onClick={() => {
                    window.open('/app/founder-journal', '_blank');
                  }}
                >
                  <span>✍️ Journal Entry</span>
                  <span className="text-sm opacity-75">Reflect & grow</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Analytics Modal */}
      {showAnalytics && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-startup-navy flex items-center">
                  <BarChart3 className="w-6 h-6 mr-3 text-startup-teal" />
                  Analytics Dashboard
                </h2>
                <button
                  onClick={() => setShowAnalytics(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-startup-teal/10 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-startup-teal">{currentStreak}</div>
                  <div className="text-sm text-startup-gray">Current Streak</div>
                </div>
                <div className="bg-startup-peach/10 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-startup-peach">{tasks.filter(t => t.completed).length}</div>
                  <div className="text-sm text-startup-gray">Tasks Completed</div>
                </div>
                <div className="bg-startup-gold/10 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-startup-gold">{Math.round((tasks.filter(t => t.completed).length / Math.max(tasks.length, 1)) * 100)}%</div>
                  <div className="text-sm text-startup-gray">Completion Rate</div>
                </div>
                <div className="bg-startup-navy/10 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-startup-navy">{daysRemaining}</div>
                  <div className="text-sm text-startup-gray">Days Remaining</div>
                </div>
              </div>

              {/* Progress Chart */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-startup-navy mb-4 flex items-center">
                  <TrendingUp className="w-5 h-5 mr-2 text-startup-teal" />
                  Weekly Progress
                </h3>
                <div className="space-y-3">
                  {streakData.map((day, index) => {
                    const dayName = new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' });
                    const completionRate = day.totalTasks > 0 ? (day.tasksCompleted / day.totalTasks) * 100 : 0;
                    
                    return (
                      <div key={day.date} className="flex items-center space-x-3">
                        <div className="w-8 text-sm font-medium text-startup-gray">{dayName}</div>
                        <div className="flex-1 bg-gray-200 rounded-full h-3 relative overflow-hidden">
                          <div 
                            className="bg-startup-teal h-full rounded-full transition-all duration-500"
                            style={{ width: `${completionRate}%` }}
                          />
                        </div>
                        <div className="w-16 text-sm text-startup-gray text-right">
                          {day.tasksCompleted}/{day.totalTasks}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Task Categories */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-startup-navy mb-4">Task Priority Breakdown</h3>
                  <div className="space-y-3">
                    {['high', 'medium', 'low'].map(priority => {
                      const priorityTasks = tasks.filter(t => t.priority === priority);
                      const completed = priorityTasks.filter(t => t.completed).length;
                      const total = priorityTasks.length;
                      const percentage = total > 0 ? (completed / total) * 100 : 0;
                      
                      return (
                        <div key={priority} className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              priority === 'high' ? 'bg-red-500' : 
                              priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                            }`} />
                            <span className="capitalize text-sm font-medium">{priority}</span>
                          </div>
                          <div className="text-sm text-startup-gray">
                            {completed}/{total} ({Math.round(percentage)}%)
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-startup-navy mb-4">Sprint Progress</h3>
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-startup-teal mb-2">
                        {tasks.filter(t => t.completed).length}/{tasks.length}
                      </div>
                      <div className="text-sm text-startup-gray">Tasks Completed</div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div 
                        className="bg-gradient-to-r from-startup-teal to-startup-peach h-4 rounded-full transition-all duration-500"
                        style={{ width: `${Math.round((tasks.filter(t => t.completed).length / Math.max(tasks.length, 1)) * 100)}%` }}
                      />
                    </div>
                    <div className="text-center text-sm text-startup-gray">
                      {Math.round((tasks.filter(t => t.completed).length / Math.max(tasks.length, 1)) * 100)}% Complete
                    </div>
                  </div>
                </div>
              </div>

              {/* Motivational Insights */}
              <div className="bg-gradient-to-r from-startup-navy to-startup-teal rounded-xl p-6 text-white">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Trophy className="w-5 h-5 mr-2" />
                  Insights & Recommendations
                </h3>
                <div className="space-y-2 text-sm opacity-90">
                  {currentStreak >= 7 && (
                    <p>🔥 Amazing! You've maintained a {currentStreak}-day streak. Keep this momentum!</p>
                  )}
                  {tasks.filter(t => t.completed).length >= 10 && (
                    <p>🎯 Great progress! You've completed {tasks.filter(t => t.completed).length} tasks.</p>
                  )}
                  {daysRemaining <= 7 && (
                    <p>⏰ Sprint finale! {daysRemaining} days left - time to push for the finish line!</p>
                  )}
                  <p>💡 Focus on completing high-priority tasks first for maximum impact.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Weekly Goal Modal */}
      {showGoalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-startup-navy flex items-center">
                  <Target className="w-5 h-5 mr-2 text-startup-gold" />
                  Set Weekly Goal
                </h2>
                <button
                  onClick={() => setShowGoalModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-startup-navy mb-2">
                    What's your main focus this week?
                  </label>
                  <textarea
                    value={weeklyGoal}
                    onChange={(e) => setWeeklyGoal(e.target.value)}
                    placeholder="e.g., Complete 5 customer interviews, Launch MVP beta, Reach $1K MRR..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-startup-teal focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>

                <div className="bg-startup-teal/5 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-startup-navy mb-2">💡 Goal Setting Tips:</h4>
                  <ul className="text-xs text-startup-gray space-y-1">
                    <li>• Be specific and measurable</li>
                    <li>• Focus on outcomes, not just activities</li>
                    <li>• Make it challenging but achievable</li>
                    <li>• Align with your 30-day sprint objectives</li>
                  </ul>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowGoalModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSetWeeklyGoal}
                    disabled={!weeklyGoal.trim()}
                    className="flex-1 px-4 py-2 bg-startup-gold text-white rounded-lg hover:bg-startup-gold/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Set Goal
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BossMode;
